import { memo } from 'react';
import { CartContainer, Badge, Dropdown } from '@aimsos/ui';
import { MoreHorizontal } from 'lucide-react';
import { StatusPill, IntegrationProviderLogo } from '../../../components';
import { formatRelativeTime, CHIP_BASE, CHIP_LABEL, CHIP_TONE } from '../../../utils';

// Managed chip reuses the shared outlined-chip tones; the uppercase + tracking
// treatment is this row's own modifier on top of the token.
const MANAGED_CHIP = `!uppercase !tracking-[0.04em]`;
const MANAGED_TONE = { aims: CHIP_TONE.accent, byok: CHIP_TONE.purple };

/**
 * Compact instance row for the Instances tab. Uses CartContainer + Badge /
 * StatusPill / Dropdown (same UI kit as before) but a two-line Tailwind layout
 * matching the prototype: logo · name + id on top; status, managed, caps,
 * last run and connected-by on the second line.
 */
function InstanceRowCard({ t, instance, providerSlug, providerName, onOpen }) {
  const managedKey = instance.managed === 'byok' ? 'byok' : 'aims';
  const lastRun = formatRelativeTime(instance.last_run, t);
  const connectedBy = instance.connected_by?.name ?? '—';
  const stop = (e) => e.stopPropagation();

  return (
    <CartContainer size="S" className="cursor-pointer" onClick={() => onOpen(instance.id)}>
      <div className="flex min-w-0 items-center gap-3">
        <IntegrationProviderLogo
          slug={providerSlug}
          name={providerName ?? instance.name}
          size="small"
        />

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-content">{instance.name}</h4>
            {instance.slug ? (
              <Badge variant="neutral" size="small" className={`${CHIP_BASE} ${CHIP_TONE.neutral}`}>
                <span className={`${CHIP_LABEL} font-mono`}>{instance.slug}</span>
              </Badge>
            ) : null}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-content-muted">
            <StatusPill status={instance.status} surface="detail" />
            <Badge
              variant="neutral"
              size="small"
              className={`${CHIP_BASE} ${MANAGED_TONE[managedKey]} ${MANAGED_CHIP}`}
            >
              <span className={CHIP_LABEL}>
                {t(`integrations.provider.instancesTab.managed.${managedKey}`)}
              </span>
            </Badge>
            <span>
              {t('integrations.provider.instancesTab.capabilitiesActive', {
                count: instance.capabilities_active ?? 0,
              })}
            </span>
            <span aria-hidden="true" className="text-content-faint">
              ·
            </span>
            <span>{t('integrations.provider.instancesTab.lastRunLabel', { time: lastRun || '—' })}</span>
            <span aria-hidden="true" className="text-content-faint">
              ·
            </span>
            <span>{t('integrations.provider.instancesTab.connectedBy', { user: connectedBy })}</span>
          </div>
        </div>

        <div className="shrink-0" onClick={stop} onKeyDown={stop}>
          <Dropdown
            align="right"
            usePortal
            trigger={
              <button
                type="button"
                className="inline-flex size-8 items-center justify-center rounded-md text-content-muted transition-colors hover:bg-surface-hover-subtle hover:text-content"
                aria-label={t('integrations.provider.instancesTab.menuAria')}
              >
                <MoreHorizontal size={16} aria-hidden="true" />
              </button>
            }
          >
            <Dropdown.Item onClick={() => onOpen(instance.id)}>
              {t('integrations.provider.instancesTab.menuOpen')}
            </Dropdown.Item>
          </Dropdown>
        </div>
      </div>
    </CartContainer>
  );
}

export default memo(InstanceRowCard);
